import DialogHeader from "@shared/ui/dialog/DialogHeader";
import {
    Box,
    Avatar,
    Button,
    Checkbox,
    CircularProgress,
    DialogActions,
    DialogContent,
    FormControlLabel,
    IconButton,
    List,
    ListItemButton,
    Pagination,
    Stack,
    Switch,
    Typography,
    useTheme,
} from "@mui/material";
import { Close, WarningAmber } from "@mui/icons-material";
import { Product } from "@domain/product/models/Product";
import { isSupplierLinkedToAllProducts } from "@domain/product/isSupplierLinkedToAllProducts";
import { useSuppliers } from "@features/suppliers/hooks/useSuppliers";
import { InventoryStatus, useInventory } from "@features/inventory";
import { APP_CONFIG } from "@config/app.config";
import ProductsFilter from "@shared/ui/filters/ProductsFilter";
import { useState, useMemo, useEffect, useRef } from "react";
import { useProductsFilter } from "@features/products/hooks/useProductsFilter";
import ActiveFilters from "@shared/ui/filters/ActiveFilters";
import type { ProductsFilterSectionKey } from "@shared/ui/filters/productFilterSectionKeys";
import EmptyState from "@shared/ui/common/EmptyState";
import type { InventoryFilter } from "@domain/inventory/models/InventoryFilter";
import { SelectChip } from "@shared/ui/common/SelectChip";


interface PurchaseOrderProductsProps {
    addedProducts?: Product[];
    selectedProducts?: Product[];
    supplierId?: string;
    initialMaxOrderWithinWeeks?: number;
    handleClose: () => void;
    handleNext: (products: Product[], supplierId?: string) => void | Promise<void>;
}

export const PurchaseOrderProducts = ({
    handleClose,
    handleNext,
    supplierId,
    addedProducts,
    selectedProducts: initialSelectedProducts,
    initialMaxOrderWithinWeeks,
}: PurchaseOrderProductsProps) => {

    const theme = useTheme();

    const { suppliers } = useSuppliers();
    const showSupplierChips = suppliers.length > 0 && !supplierId;
    const [selectedSupplierId, setSelectedSupplierId] = useState<string | undefined>();

    const supplierFilterId = supplierId ?? selectedSupplierId;

    const lockedSupplierName = useMemo(() => {
        if (!supplierId) return undefined;
        return suppliers.find(s => s.id === supplierId)?.name;
    }, [supplierId, suppliers]);

    const productsFilterDisabledSections: readonly ProductsFilterSectionKey[] | undefined = supplierId
        ? ["supplier"]
        : undefined;

    const [maxOrderWithinWeeks, setMaxOrderWithinWeeks] = useState<number | undefined>(
        initialMaxOrderWithinWeeks,
    );

    const { filterCriteria, page, setPage, limit } = useProductsFilter(50);

    const inventoryFilter = useMemo((): InventoryFilter => ({
        ...filterCriteria,
        page,
        limit,
        purchasable: true,
        ...(supplierFilterId ? { supplierIds: [supplierFilterId] } : {}),
        ...(maxOrderWithinWeeks != null ? { maxOrderWithinWeeks } : {}),
    }), [page, limit, filterCriteria, supplierFilterId, maxOrderWithinWeeks]);

    const { inventory, isLoading, isFetching } = useInventory(inventoryFilter);

    const [selectedProducts, setSelectedProducts] = useState<Product[]>(initialSelectedProducts || []);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isMountedRef = useRef(true);

    // Non-filter selection state (supplier chip, restock toggle) isn't tracked by
    // useProductsFilter's reset, so it still needs its own page reset.
    useEffect(() => {
        setPage(1);
    }, [selectedSupplierId, supplierId, maxOrderWithinWeeks, setPage]);


    const selectableOnPage = useMemo(() => {
        if (inventory?.status !== 'success' || !inventory.data?.length) return [];
        return inventory.data.map(pi => pi.product);
    }, [inventory]);

    const selectedCountOnPage = useMemo(
        () => selectableOnPage.filter(p => selectedProducts.some(s => s.id === p.id)).length,
        [selectableOnPage, selectedProducts]
    );

    const allSelectableOnPageSelected =
        selectableOnPage.length > 0 && selectedCountOnPage === selectableOnPage.length;
    const someSelectableOnPageSelected =
        selectedCountOnPage > 0 && !allSelectableOnPageSelected;

    const toggleSelectAllOnPage = () => {
        if (selectableOnPage.length === 0 || isSubmitting) return;
        setSelectedProducts(prev => {
            if (allSelectableOnPageSelected) {
                const pageIds = new Set(selectableOnPage.map(p => p.id));
                return prev.filter(p => !pageIds.has(p.id));
            }
            const byId = new Map(prev.map(p => [p.id, p]));
            selectableOnPage.forEach(p => byId.set(p.id, p));
            return Array.from(byId.values());
        });
    };

    const toggleProduct = (product: Product) => {
        if (isSubmitting) return;
        if (selectedProducts.some(p => p.id === product.id)) {
            setSelectedProducts(selectedProducts.filter(p => p.id !== product.id));
        } else {
            setSelectedProducts([...selectedProducts, product]);
        }
    };

    const handleAddProducts = async () => {
        if (selectedProducts.length === 0 || isSubmitting) return;
        setIsSubmitting(true);
        try {
            await Promise.resolve(handleNext(selectedProducts, supplierId ?? selectedSupplierId));
        } finally {
            if (isMountedRef.current) {
                setIsSubmitting(false);
            }
        }
    };

    return (
        <>
            <DialogHeader>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" component="div" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                        Select the product you want to purchase
                    </Typography>
                    <IconButton onClick={handleClose} disabled={isSubmitting}>
                        <Close />
                    </IconButton>
                </Stack>
            </DialogHeader>

            <DialogContent sx={{ p: 0 }}>
                {supplierId && (
                    <Stack px={4} py={2} gap={0.5} borderBottom={1} borderColor="divider">
                        <Typography variant="caption" color="text.secondary" component="div">
                            Supplier
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                            {lockedSupplierName ?? supplierId}
                        </Typography>
                    </Stack>
                )}
                {showSupplierChips && (
                    <Stack borderBottom={1} borderColor="divider" bgcolor="background.default" >
                        <Stack direction="row" overflow="auto" px={4} py={2} gap={1} >
                            <SelectChip
                                label="All products"
                                isSelected={selectedSupplierId === undefined}
                                onSelect={() => setSelectedSupplierId(undefined)}
                            />

                            {suppliers.map(s => {
                                const disabledBySelection =
                                    selectedProducts.length > 0 &&
                                    !isSupplierLinkedToAllProducts(s.id, selectedProducts, suppliers);

                                return (
                                    <Box key={s.id} component="span" sx={{ display: "inline-flex" }}>
                                        <SelectChip
                                            label={s.name}
                                            isSelected={selectedSupplierId === s.id}
                                            onSelect={() => setSelectedSupplierId(s.id)}
                                            disabled={disabledBySelection}
                                        />
                                    </Box>
                                );
                            })}

                        </Stack>
                    </Stack>
                )}

                <Stack
                    borderBottom={1}
                    borderColor="divider"
                    bgcolor="background.paper"
                    position="sticky"
                    top={0}
                    zIndex={99}
                    direction="row"
                    alignItems="center"
                    px={4}
                    py={2}
                    spacing={2}
                    justifyContent="space-between"
                >
                    <Stack direction="row" alignItems="center" flex={1} spacing={1}>
                        <Checkbox
                            checked={allSelectableOnPageSelected}
                            indeterminate={someSelectableOnPageSelected}
                            disabled={selectableOnPage.length === 0 || isLoading || isFetching || isSubmitting}
                            onChange={toggleSelectAllOnPage}
                            inputProps={{ 'aria-label': 'Select all products on this page' }}
                        />
                        <Typography variant="body2" color="text.secondary" component="div">
                            ({selectedCountOnPage}/{selectableOnPage.length})
                        </Typography>
                    </Stack>

                    <ActiveFilters disabledSections={productsFilterDisabledSections} />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={maxOrderWithinWeeks === 0}
                                onChange={(_, checked) =>
                                    setMaxOrderWithinWeeks(checked ? 0 : undefined)
                                }
                                inputProps={{ 'aria-label': 'Needs restock' }}
                            />
                        }
                        label={<Typography variant="body2" color="text.secondary" noWrap>Needs Restock</Typography>}
                        labelPlacement="start"
                    />

                    <ProductsFilter
                        disabledSections={productsFilterDisabledSections}
                    />
                </Stack>

                {/* Product Selection */}
                <Stack gap={0}>

                    {isLoading || isFetching ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : inventory?.status === 'success' && inventory?.data?.length === 0 ? (

                        <EmptyState
                            title="No products found"
                            description="No products found with the current filters"
                        />
                    ) : (

                        <Stack gap={0}>
                            <List
                                disablePadding>
                                {inventory?.status === 'success' && inventory?.data?.map((productInventory) => {
                                    const inventoryItem = inventory?.data.find(i => i.product.id === productInventory.product.id);

                                    return (
                                        <ListItemButton
                                            key={productInventory.product.id}
                                            divider
                                            disabled={isSubmitting}
                                            onClick={() => toggleProduct(productInventory.product)}

                                        >
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={2}
                                                p={2}
                                                flex={1}
                                            >

                                                <Checkbox
                                                    checked={selectedProducts.some(p => p.id === productInventory.product.id)}
                                                    disabled={isSubmitting}
                                                    onChange={() => toggleProduct(productInventory.product)}
                                                    onClick={e => e.stopPropagation()}
                                                />


                                                <Avatar
                                                    variant="rounded"
                                                    sx={{
                                                        border: 1,
                                                        borderColor: APP_CONFIG.ui.theme.colors.border,
                                                        width: 40,
                                                        height: 40,
                                                    }}
                                                    src={productInventory.product.image}
                                                    alt={productInventory.product.title}
                                                />

                                                <Stack direction="column" gap={0.5} flex={1}>

                                                    <Typography variant="subtitle2" fontWeight="medium" flexWrap="wrap"
                                                        sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: addedProducts?.some(p => p.id === productInventory.product.id) ? 1 : 2,
                                                            WebkitBoxOrient: 'vertical',
                                                        }}
                                                    >
                                                        {productInventory.product.title}
                                                    </Typography>

                                                    {addedProducts?.some(p => p.id === productInventory.product.id) && (
                                                        <Box display="flex" alignItems="center" gap={0.5}>
                                                            <WarningAmber sx={{ fontSize: 14, color: theme.palette.warning.main }} />
                                                            <Typography variant="caption" color={theme.palette.warning.main}>
                                                                Already added to Purchase Order
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                </Stack>

                                                <Stack
                                                    direction="column"
                                                    gap={1}
                                                    sx={{ width: 150, minWidth: 0, flexShrink: 1 }}
                                                >
                                                    <Box position="relative">
                                                        <Typography variant="body2" color="text.primary">
                                                            {productInventory.quantity} items available
                                                        </Typography>

                                                        <Typography variant="body2" color="text.primary">
                                                            {inventoryItem?.purchaseOrderItems?.reduce((acc, purchaseOrder) => acc + (purchaseOrder.quantity ?? 0), 0)} items in transit
                                                        </Typography>

                                                    </Box>
                                                </Stack>



                                                {inventoryItem && (
                                                    <Stack
                                                        sx={{
                                                            flex: '0 1 250px',
                                                            minWidth: 0,
                                                            maxWidth: 250,
                                                        }}
                                                    >
                                                        <InventoryStatus
                                                            inventory={inventoryItem}
                                                        />
                                                    </Stack>
                                                )}


                                            </Stack>
                                        </ListItemButton>

                                    );
                                })}
                            </List>


                            {inventory?.status === 'success' && inventory?.totalPages > 1 && (
                                <Stack p={2} direction="row" justifyContent="center" alignItems="center">
                                    <Pagination
                                        count={inventory?.totalPages || 0}
                                        page={page}
                                        onChange={(_, value) => setPage(value)}
                                        color="primary"
                                        size="large"
                                        shape="rounded"
                                        showFirstButton
                                        showLastButton
                                    />
                                </Stack>
                            )}
                        </Stack>
                    )}


                </Stack>


            </DialogContent >

            < DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider', gap: 2 }
            }>

                <Typography variant="body2" color="text.secondary" component="div" noWrap>
                    Purchase order {selectedSupplierId ? `for ${suppliers.find(s => s.id === selectedSupplierId)?.name}` : "without supplier"}
                </Typography>
                <Button
                    sx={{ whiteSpace: 'nowrap' }}
                    variant="contained"
                    disabled={selectedProducts.length === 0 || isSubmitting}
                    onClick={() => void handleAddProducts()}
                >
                    {isSubmitting ? (
                        <CircularProgress size={24} color="inherit" />
                    ) : (
                        'Add Products'
                    )}
                </Button>
            </DialogActions >
        </>
    );
};